import React, {useEffect} from "react";
import {NavLink, useLocation, useNavigate} from "react-router-dom";
import {useAuth, UserRole} from "../../AuthContext";
import FastAnimatedOutlet from "../../components/Animation/FastAnimatedOutlet";
import EnvDropdown from "../../components/EnvDropdown";
import {
  Home,
  Users,
  FileText,
  Calendar,
  Crown,
  CalendarDays,
  UserCog,
  CheckCircle,
  BarChart3,
  MessageSquare,
  Settings,
  LucideIcon,
} from "lucide-react";
import "../../styles/env.css";

interface NavigationItem {
  label: string;
  path: string;
  icon: LucideIcon;
}

interface NavigationSection {
  header: string;
  role?: UserRole;
  items: NavigationItem[];
}

const EnvLayout: React.FC = () => {
  const {roles, user, loading} = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Check email verification status for protected routes
  useEffect(() => {
    if (loading) return; // Wait for auth to load

    if (!user) {
      // No user - redirect to login and preserve intended destination
      const redirect = encodeURIComponent(location.pathname + location.search);
      navigate(`/auth/login?redirect=${redirect}`);
      return;
    }

    // Check if user's email is verified (for email/password accounts)
    if (user.isEmailVerified === false) {
      // User is not verified - redirect to email verification page
      navigate("/auth/email-verification");
      return;
    }
  }, [user, loading, navigate]);

  // Prefer pretty URLs: if user lands on /env/*, redirect to /* equivalent
  useEffect(() => {
    if (location.pathname === "/env") {
      navigate("/overview", {replace: true});
      return;
    }
    if (location.pathname.startsWith("/env/")) {
      const prettyPath = location.pathname.replace(/^\/env\//, "/");
      navigate(prettyPath + location.search, {replace: true});
    }
  }, [location.pathname, location.search, navigate]);

  // Helper function to check if user has access to a section
  const hasAccessToSection = (sectionRole?: UserRole): boolean => {
    if (!sectionRole) return true; // No role requirement means always accessible
    if (sectionRole === "moderator") return roles.includes("moderator"); // Only moderators see moderator section
    if (roles.includes("admin")) return true; // Admins have access to everything except moderator section
    if (
      sectionRole === "player" &&
      (roles.includes("dm") || roles.includes("moderator"))
    )
      return true; // DMs and moderators have access to player pages
    if (sectionRole === "dm" && roles.includes("moderator")) return true; // Moderators have access to DM pages
    return roles.includes(sectionRole); // Other users need the specific role
  };

  // Build navigation sections dynamically
  const dynamicSections: NavigationSection[] = [
    {
      header: "PAGES",
      items: [
        {label: "Dashboard", path: "/overview", icon: Home},
        {label: "Calendar", path: "/calendar", icon: Calendar},
      ],
    },
    {
      header: "PLAYER",
      role: "player" as UserRole,
      items: [
        {label: "Characters", path: "/characters", icon: Users},
        {label: "Sessions", path: "/sessions", icon: FileText},
        {label: "Titans", path: "/titans", icon: Crown},
        // Only show "Become a DM" link to players who don't have DM status
        ...(roles.includes("player") && !roles.includes("dm")
          ? [{label: "Become a DM", path: "/dm-signup", icon: Crown}]
          : []),
      ],
    },
    {
      header: "DM",
      role: "dm" as UserRole,
      items: [
        {label: "Titan Management", path: "/titanmanagement", icon: Crown},
        {
          label: "Session Management",
          path: "/sessionmanagement",
          icon: CalendarDays,
        },
        {
          label: "Session Logs",
          path: "/session-logs",
          icon: FileText,
        },
      ],
    },
    {
      header: "MODERATOR",
      role: "moderator" as UserRole,
      items: [
        {label: "DM Approvals", path: "/dm-approvals", icon: CheckCircle},
        {
          label: "Feedback Management",
          path: "/feedback-management",
          icon: MessageSquare,
        },
      ],
    },
    {
      header: "ADMIN",
      role: "admin" as UserRole,
      items: [
        {label: "User Management", path: "/usermanagement", icon: UserCog},
        {label: "DM Approvals", path: "/dm-approvals", icon: CheckCircle},
        {label: "System Logs", path: "/system-logs", icon: BarChart3},
        {label: "Admin Config", path: "/admin-config", icon: Settings},
      ],
    },
    {
      header: "Settings",
      items: [{label: "Settings", path: "/settings", icon: Settings}],
    },
  ];

  // Show loading state while checking auth/verification
  if (loading) {
    return (
      <div className="page-layout">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            flexDirection: "column",
          }}
        >
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Don't render anything if user is not authenticated or not verified
  // (useEffect will handle redirects)
  if (!user || user.isEmailVerified === false) {
    return null;
  }

  return (
    <div className="page-layout">
      {/* Mobile Environment Dropdown */}
      <EnvDropdown
        sections={dynamicSections.filter((section) =>
          hasAccessToSection(section.role)
        )}
        currentPath={location.pathname}
        onNavigate={() => {}}
      />

      <aside className="env-sidebar">
        {dynamicSections
          .filter((section) => hasAccessToSection(section.role))
          .map((section, index, filteredSections) => {
            return (
              <React.Fragment key={section.header}>
                <div className="section-group">
                  {section.items.map((item) => {
                    const IconComponent = item.icon;
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        className={({isActive}) =>
                          isActive
                            ? "sidebar-link sidebar-link-active"
                            : "sidebar-link"
                        }
                      >
                        <IconComponent size={18} className="sidebar-icon" />
                        {item.label}
                      </NavLink>
                    );
                  })}
                </div>
                {/* Add divider between sections, but not after the last section */}
                {index < filteredSections.length - 1 && (
                  <hr className="section-divider" />
                )}
              </React.Fragment>
            );
          })}
      </aside>
      <main className="env-main">
        <FastAnimatedOutlet />
      </main>
    </div>
  );
};

export default EnvLayout;
